import { Component, DestroyRef, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { ActivatedRoute } from '@angular/router';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import {
  NombreControlAhorroFiscal,
  ValorFormularioAhorroFiscal,
  FilaAhorroEmpresa,
  FilaAhorroEmpleado
} from '../../../../interfaces/calculator.interface';

@Component({
  selector: 'app-ahorro-fiscal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CaButtonComponent
  ],
  templateUrl: './ahorro-fiscal.component.html',
  styleUrls: ['./ahorro-fiscal.component.scss']
})
export class AhorroFiscalComponent implements OnInit {
  @Input() data: { prima?: string | number } | null = null;
  @ViewChild('printArea', { static: false }) printArea!: ElementRef<HTMLElement>;

  form!: FormGroup;
  prima: string | null = null;
  employeeRows: FilaAhorroEmpleado[] = [];
  companyRows: FilaAhorroEmpresa[] = [];

  private conRetribucionRetencionInicializada = false;
  private conRetribucionRetencionModificadaManualmente = false;

  PARAMETERS_CALCULATOR: any[] = [
    {
      desde: 0,
      hasta: 12450,
      retencion: 19
    },
    {
      desde: 12450.01,
      hasta: 20200,
      retencion: 24
    },
    {
      desde: 20200.01,
      hasta: 35200,
      retencion: 30
    },
    {
      desde: 35200.01,
      hasta: 60000,
      retencion: 37
    },
    {
      desde: 60000.01,
      hasta: 300000,
      retencion: 45
    },
    {
      desde: 300001,
      hasta: Infinity,
      retencion: 47
    }
  ];

  constructor(
    private destroyRef: DestroyRef,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initFormCalculator();
  }

  initFormCalculator(): void {
    this.conRetribucionRetencionInicializada = false;
    this.conRetribucionRetencionModificadaManualmente = false;

    this.prima = this.data?.prima != null
      ? String(this.data.prima).replace('.', ',')
      : this.route.snapshot.paramMap.get('prima');

    this.form = new FormGroup({

      // salario bruto empleado
      salarioBrutoSinRetribucion: new FormControl('40.000,00', { nonNullable: true }),
      salarioBrutoConRetribucion: new FormControl('40.000,00', { nonNullable: true }),

      primaMensual: new FormControl(this.prima ?? '', { nonNullable: true }),

      numeroAseguradosEmpleado: new FormControl(3, { nonNullable: true }),

      sinRetribucionRetencion: new FormControl('', {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(100)]
      }),

      conRetribucionRetencion: new FormControl('', {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(100)]
      }),

      irpfSinRetribucion: new FormControl('', {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(100)]
      }),

      irpfConRetribucion: new FormControl('', {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(100)]
      }),

      tipoImpuestoSociedades: new FormControl(25, {
        nonNullable: true,
        validators: [Validators.min(0), Validators.max(100)]
      }),

      numeroAseguradosEmpresa: new FormControl(30, { nonNullable: true })
    });

    this.listenFormChanges();
    this.buildRows();
  }

  private listenConRetribucionRetencion(): void {
    this.form.get('conRetribucionRetencion')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.conRetribucionRetencionModificadaManualmente = true;

        const sinControl = this.form.get('sinRetribucionRetencion');

        if (
          sinControl &&
          this.parseNumericValue(sinControl.value) !== this.parseNumericValue(value)
        ) {
          sinControl.setValue(value, { emitEvent: false });
        }
      });
  }

  private listenSalarioBrutoConRetribucion(): void {
    this.form.get('salarioBrutoConRetribucion')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        const salarioBrutoConRetribucion = this.parseNumericValue(value);

        const retencion = this.PARAMETERS_CALCULATOR.find((item) =>
          salarioBrutoConRetribucion >= item.desde &&
          salarioBrutoConRetribucion <= item.hasta
        )?.retencion ?? 0;

        this.conRetribucionRetencionModificadaManualmente = false;

        this.form.get('conRetribucionRetencion')?.setValue(retencion, { emitEvent: false });
        this.form.get('sinRetribucionRetencion')?.setValue(retencion, { emitEvent: false });
        this.form.get('sinRetribucionRetencion')?.setValue(retencion, { emitEvent: false });
      });
  }

  private listenGeneralFormChanges(): void {
    this.form.valueChanges
      .pipe(
        startWith(this.form.getRawValue()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.limitPercentageValue('irpfSinRetribucion');
        this.limitPercentageValue('irpfConRetribucion');
        this.limitPercentageValue('tipoImpuestoSociedades');
        this.limitPercentageValue('sinRetribucionRetencion');
        this.limitPercentageValue('conRetribucionRetencion');

        this.buildRows();
      });
  }

  private listenFormChanges(): void {
    this.listenConRetribucionRetencion();
    this.listenSalarioBrutoConRetribucion();
    this.listenGeneralFormChanges();
  }

  private limitPercentageValue(
    controlName:
      | 'irpfSinRetribucion'
      | 'irpfConRetribucion'
      | 'tipoImpuestoSociedades'
      | 'sinRetribucionRetencion'
      | 'conRetribucionRetencion'
  ): void {
    const control = this.form.get(controlName);

    if (!control) {
      return;
    }

    const value = this.parseNumericValue(control.value);

    if (value > 100 && value !== 100) {
      control.setValue(100, { emitEvent: false });
    }

    if (value < 0 && value !== 0) {
      control.setValue(0, { emitEvent: false });
    }
  }

  private buildRows(): void {
    const formValue = this.form.getRawValue() as ValorFormularioAhorroFiscal;

    const salarioBrutoSinRetribucion = this.parseNumericValue(formValue.salarioBrutoSinRetribucion);

    //Prima Bruto Salario
    const salarioBrutoConRetribucion = this.parseNumericValue(formValue.salarioBrutoConRetribucion);

    //Prima mensual asegurado
    const primaMensual = this.parseNumericValue(formValue.primaMensual);

    //Nº de asegurados ene la polioza del empleado
    const numeroAseguradosEmpleado = this.parseNumericValue(formValue.numeroAseguradosEmpleado);

    const tipoImpuestoSociedades = this.parseNumericValue(formValue.tipoImpuestoSociedades);
    const numeroAseguradosEmpresa = this.parseNumericValue(formValue.numeroAseguradosEmpresa);

    // Prima anual de la poliza del empleado
    const primaAnualEmpleado = (numeroAseguradosEmpleado * primaMensual) * 12;

    const baseImponibleSinRetribucion = salarioBrutoConRetribucion;

    // X
    const baseImponibleConRetribucion = salarioBrutoConRetribucion - primaAnualEmpleado;

    const retencion = this.PARAMETERS_CALCULATOR.find((value) =>
      salarioBrutoConRetribucion >= value.desde &&
      salarioBrutoConRetribucion <= value.hasta
    )?.retencion ?? 0;

    const sinRetribucionRetencionControl = this.form.get('sinRetribucionRetencion');

    if (
      sinRetribucionRetencionControl &&
      !this.conRetribucionRetencionModificadaManualmente &&
      this.parseNumericValue(sinRetribucionRetencionControl.value) !== retencion
    ) {
      sinRetribucionRetencionControl.setValue(retencion, { emitEvent: false });
    }

    const conRetribucionRetencionControl = this.form.get('conRetribucionRetencion');

    if (conRetribucionRetencionControl && !this.conRetribucionRetencionInicializada) {
      conRetribucionRetencionControl.setValue(retencion, { emitEvent: false });
      this.conRetribucionRetencionInicializada = true;
    }

    const sinRetribucionRetencion = this.parseNumericValue(
      sinRetribucionRetencionControl?.value ?? retencion
    );

    const conRetribucionRetencion = this.parseNumericValue(
      conRetribucionRetencionControl?.value ?? retencion
    );

    const irpfSinRetribucionImporte = baseImponibleSinRetribucion * (sinRetribucionRetencion / 100);

    const valorEspecie = primaAnualEmpleado - (500 * numeroAseguradosEmpleado) < 0
      ? 0
      : Math.round(primaAnualEmpleado - (500 * numeroAseguradosEmpleado));

    console.log('valorEspecie', valorEspecie);

    const irpfConRetribucionImporte = baseImponibleConRetribucion * (conRetribucionRetencion / 100);

    //aquí
    const ahorroFiscalEmpleado = (primaAnualEmpleado - valorEspecie) * (conRetribucionRetencion / 100);

    const primaAnualPorAseguradoEmpresa = primaMensual * 12;
    const primaAnualEmpresa = primaAnualPorAseguradoEmpresa * numeroAseguradosEmpresa;
    const ahorroFiscalEmpresa = (tipoImpuestoSociedades / 100) * primaAnualPorAseguradoEmpresa * numeroAseguradosEmpresa;

    this.employeeRows = [
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.SALARIO',
        sinRetribucionFlexible: this.formatCurrency(salarioBrutoConRetribucion),
        conRetribucionFlexible: this.formatCurrency(salarioBrutoConRetribucion),
        controlConRetribucion: 'salarioBrutoConRetribucion',
        sufijoConRetribucion: '€'
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.PRIMA-MENSUAL',
        sinRetribucionFlexible: '',
        conRetribucionFlexible: this.formatCurrency(primaMensual)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.NUM_ASEGURADOS',
        sinRetribucionFlexible: '',
        conRetribucionFlexible: '',
        controlConRetribucion: 'numeroAseguradosEmpleado'
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.PRIMA-ANUAL',
        sinRetribucionFlexible: '',
        conRetribucionFlexible: this.formatCurrency(primaAnualEmpleado)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.BASE-IMPONIBLE',
        sinRetribucionFlexible: this.formatCurrency(salarioBrutoConRetribucion),
        conRetribucionFlexible: this.formatCurrency(baseImponibleConRetribucion)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.IRPF-MARGINAL',
        sinRetribucionFlexible: `${sinRetribucionRetencion} %`,
        conRetribucionFlexible: '',
        controlConRetribucion: 'conRetribucionRetencion',
        sufijoConRetribucion: '%'
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.IRPF',
        sinRetribucionFlexible: this.formatCurrency(irpfSinRetribucionImporte),
        conRetribucionFlexible: this.formatCurrency(irpfConRetribucionImporte)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.EMPLEADO.AHORRO-FISCAL',
        sinRetribucionFlexible: '',
        conRetribucionFlexible: this.formatCurrency(ahorroFiscalEmpleado),
        destacado: true
      }
    ];

    this.companyRows = [
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.COMPANIA.IMPUESTOS',
        valor: '',
        control: 'tipoImpuestoSociedades',
        sufijo: '%'
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.COMPANIA.PRIMA-MENSUAL',
        valor: this.formatCurrency(primaMensual)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.COMPANIA.PRIMA-ANUAL',
        valor: this.formatCurrency(primaAnualPorAseguradoEmpresa)
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.COMPANIA.NUM_ASEGURADOS',
        valor: '',
        control: 'numeroAseguradosEmpresa'
      },
      {
        etiqueta: 'PYMES.AHORRO-FISCAL.COMPANIA.AHORRO-IMPUESTOS',
        valor: this.formatCurrency(ahorroFiscalEmpresa),
        destacado: true
      }
    ];
  }

  onEditableInputFocus(
    controlName: NombreControlAhorroFiscal | undefined,
    suffix?: string
  ): void {
    if (!controlName || suffix !== '€') {
      return;
    }

    const control = this.form.get(controlName);

    if (!control) {
      return;
    }

    const numericValue = this.parseNumericValue(control.value);

    control.setValue(this.formatEditablePlainValue(numericValue), {
      emitEvent: false
    });
  }

  onEditableInputBlur(
    controlName: NombreControlAhorroFiscal | undefined,
    suffix?: string
  ): void {
    if (!controlName || suffix !== '€') {
      return;
    }

    const control = this.form.get(controlName);

    if (!control) {
      return;
    }

    const numericValue = this.parseNumericValue(control.value);

    control.setValue(this.formatEditableEuroValue(numericValue));
  }

  private parseNumericValue(value: number | string | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    if (!value) {
      return 0;
    }

    const cleanValue = value
      .toString()
      .replace(/[^\d,.-]/g, '')
      .trim();

    const normalizedValue = cleanValue.includes(',')
      ? cleanValue.replace(/\./g, '').replace(',', '.')
      : cleanValue;

    return Number(normalizedValue) || 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  private formatEditableEuroValue(value: number): string {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  private formatEditablePlainValue(value: number): string {
    return Number.isInteger(value)
      ? String(value)
      : String(value).replace('.', ',');
  }

  async onImprimir(): Promise<void> {
    if (!this.printArea) {
      return;
    }

    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      return;
    }

    const element = this.printArea.nativeElement;

    if ('fonts' in document) {
      await document.fonts.ready;
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDocument: Document) => {
        const clonedRoot = clonedDocument.querySelector('.ahorro-fiscal') as HTMLElement | null;

        if (!clonedRoot) {
          return;
        }

        clonedRoot.querySelectorAll('.comparison-table__value, .simple-table__value, .table-cell-value').forEach((node) => {
          const el = node as HTMLElement;
          el.style.overflow = 'visible';
          el.style.whiteSpace = 'nowrap';
        });

        clonedRoot.querySelectorAll('input.table-input').forEach((node) => {
          const input = node as HTMLInputElement;
          const computed = clonedDocument.defaultView?.getComputedStyle(input);

          const span = clonedDocument.createElement('span');
          span.textContent = input.value;
          span.style.display = 'inline-block';
          span.style.width = computed?.width ?? '74px';
          span.style.minWidth = computed?.width ?? '74px';
          span.style.padding = '0';
          span.style.margin = '0';
          span.style.border = '0';
          span.style.background = 'transparent';
          span.style.font = computed?.font ?? 'inherit';
          span.style.fontSize = computed?.fontSize ?? 'inherit';
          span.style.fontFamily = computed?.fontFamily ?? 'inherit';
          span.style.fontWeight = computed?.fontWeight ?? 'inherit';
          span.style.lineHeight = computed?.lineHeight ?? 'normal';
          span.style.color = computed?.color ?? 'inherit';
          span.style.textAlign = 'center';
          span.style.verticalAlign = 'middle';
          span.style.whiteSpace = 'nowrap';
          span.style.boxSizing = 'border-box';

          input.replaceWith(span);
        });
      }
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const usableWidth = pageWidth - (margin * 2);
    const usableHeight = pageHeight - (margin * 2);

    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    const ratio = Math.min(usableWidth / imgWidth, usableHeight / imgHeight);
    const renderWidth = imgWidth * ratio;
    const renderHeight = imgHeight * ratio;
    const x = (pageWidth - renderWidth) / 2;
    const y = (pageHeight - renderHeight) / 2;

    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      x,
      y,
      renderWidth,
      renderHeight,
      undefined,
      'FAST'
    );

    const blob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(blob);

    printWindow.location.href = blobUrl;
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    };
  }

  trackByLabel(index: number, row: FilaAhorroEmpleado | FilaAhorroEmpresa): string {
    return row.etiqueta;
  }
}